import { useState, useEffect } from 'react';
import { useNotifications } from './useNotifications';
import { usePushNotifications } from './usePushNotifications';
import { useIOSInstall } from './useIOSInstall';

export interface PermissionStatus {
  location: {
    status: 'granted' | 'denied' | 'prompt' | 'unavailable';
    message: string;
  };
  notifications: {
    status: 'granted' | 'denied' | 'default' | 'unavailable';
    message: string;
  };
  camera: {
    status: 'granted' | 'denied' | 'prompt' | 'unavailable';
    message: string;
  };
  pushNotifications: {
    status: 'granted' | 'denied' | 'default' | 'unavailable';
    message: string;
    isSubscribed: boolean;
    backendSubscribed: boolean;
  };
}

export interface UsePermissionsReturn {
  permissions: PermissionStatus;
  isRequestingLocation: boolean;
  isRequestingNotifications: boolean;
  isRequestingCamera: boolean;
  isRequestingPush: boolean;
  iosInstall: {
    isIOS: boolean;
    isStandalone: boolean;
    isInstallable: boolean;
    showPrompt: boolean;
    dismissPrompt: () => void;
    markAsInstalled: () => void;
  };
  pushNotifications: ReturnType<typeof usePushNotifications>;
  checkPermissions: () => Promise<void>;
  handleLocationRequest: (onLocationRequest?: () => void) => Promise<void>;
  handleNotificationRequest: () => Promise<void>;
  handleCameraRequest: () => Promise<void>;
  handlePushNotificationRequest: () => Promise<void>;
  openBrowserSettings: () => void;
}

export const usePermissions = (): UsePermissionsReturn => {
  const [permissions, setPermissions] = useState<PermissionStatus>({
    location: { status: 'unavailable', message: 'Checking...' },
    notifications: { status: 'unavailable', message: 'Checking...' },
    camera: { status: 'unavailable', message: 'Checking...' },
    pushNotifications: { status: 'unavailable', message: 'Checking...', isSubscribed: false, backendSubscribed: false },
  });
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isRequestingNotifications, setIsRequestingNotifications] = useState(false);
  const [isRequestingCamera, setIsRequestingCamera] = useState(false);
  const [isRequestingPush, setIsRequestingPush] = useState(false);

  const { requestPermission: requestNotificationPermission, permission: notificationPermission } = useNotifications();
  const pushNotifications = usePushNotifications();
  const iosInstall = useIOSInstall();

  const checkPermissions = async () => {
    const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';

    if (isEmbedded) {
      setPermissions({
        location: { status: 'granted', message: 'Handled by native app' },
        notifications: { status: 'granted', message: 'Handled by native app' },
        camera: { status: 'granted', message: 'Handled by native app' },
        pushNotifications: { status: 'granted', message: 'Handled by native app', isSubscribed: true, backendSubscribed: true },
      });
      return;
    }

    // Location
    if (!navigator.geolocation) {
      setPermissions(prev => ({ ...prev, location: { status: 'unavailable', message: 'Location services not supported by your device' } }));
    } else {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        const messageMap: Record<string, string> = {
          granted: 'Location access enabled',
          denied: 'Location access blocked - please enable in browser settings',
          prompt: 'Location permission not set',
        };
        setPermissions(prev => ({
          ...prev,
          location: { status: result.state as 'granted' | 'denied' | 'prompt', message: messageMap[result.state] ?? '' },
        }));
      } catch {
        setPermissions(prev => ({ ...prev, location: { status: 'prompt', message: 'Click to enable location access' } }));
      }
    }

    // Notifications
    if (!('Notification' in window)) {
      setPermissions(prev => ({ ...prev, notifications: { status: 'unavailable', message: 'Notifications not supported by your device' } }));
    } else {
      const status = notificationPermission as 'granted' | 'denied' | 'default';
      const messageMap: Record<string, string> = {
        granted: 'Notifications enabled',
        denied: 'Notifications blocked - please enable in browser settings',
        default: 'Notification permission not set',
      };
      setPermissions(prev => ({ ...prev, notifications: { status, message: messageMap[status] ?? '' } }));
    }

    // Camera
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissions(prev => ({ ...prev, camera: { status: 'unavailable', message: 'Camera not supported by your device' } }));
    } else {
      try {
        const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
        const messageMap: Record<string, string> = {
          granted: 'Camera access enabled',
          denied: 'Camera access blocked - please enable in browser settings',
          prompt: 'Camera permission not set',
        };
        setPermissions(prev => ({
          ...prev,
          camera: { status: result.state as 'granted' | 'denied' | 'prompt', message: messageMap[result.state] ?? '' },
        }));
      } catch {
        setPermissions(prev => ({ ...prev, camera: { status: 'prompt', message: 'Click to enable camera access' } }));
      }
    }

    // Push notifications
    if (!pushNotifications.isSupported) {
      setPermissions(prev => ({
        ...prev,
        pushNotifications: { status: 'unavailable', message: 'Push notifications not supported', isSubscribed: false, backendSubscribed: false },
      }));
    } else {
      const isFullySubscribed = pushNotifications.isSubscribed;
      const backendSubscribed = pushNotifications.backendSubscribed;
      let message = '';
      switch (pushNotifications.permission) {
        case 'granted':
          if (isFullySubscribed) {
            message = 'Push notifications fully enabled';
          } else if (backendSubscribed && !isFullySubscribed) {
            message = 'Backend subscription found but browser subscription missing - click to re-subscribe';
          } else if (!backendSubscribed && pushNotifications.isSubscribed) {
            message = 'Browser subscribed but server registration failed - click to re-subscribe';
          } else {
            message = 'Permission granted, click to subscribe';
          }
          break;
        case 'denied':
          message = 'Push notifications blocked - please enable in browser settings';
          break;
        case 'default':
          message = 'Push notification permission not set';
          break;
      }
      setPermissions(prev => ({
        ...prev,
        pushNotifications: { status: pushNotifications.permission, message, isSubscribed: isFullySubscribed, backendSubscribed },
      }));
    }
  };

  useEffect(() => {
    checkPermissions();
  }, [notificationPermission, pushNotifications.permission, pushNotifications.isSubscribed, pushNotifications.backendSubscribed]);

  const handleLocationRequest = async (onLocationRequest?: () => void) => {
    const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';
    if (isEmbedded) {
      if (typeof (window as any).ReactNativeWebView?.getCurrentLocation === 'function') {
        (window as any).ReactNativeWebView.getCurrentLocation();
      }
      return;
    }
    
    // If already granted, disable/revoke it
    if (permissions.location.status === 'granted') {
      setPermissions(prev => ({ ...prev, location: { status: 'prompt', message: 'Location permission not set' } }));
      return;
    }
    
    if (permissions.location.status === 'denied') {
      alert("Location access is blocked. To enable:\n\n1. Click the lock icon in your browser's address bar\n2. Set \"Location\" to \"Allow\"\n3. Refresh the page");
      return;
    }
    setIsRequestingLocation(true);
    try {
      await new Promise<void>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => { resolve(); onLocationRequest?.(); },
          reject,
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
      await checkPermissions();
    } catch (error) {
      console.error('Location request failed:', error);
    } finally {
      setIsRequestingLocation(false);
    }
  };

  const handleNotificationRequest = async () => {
    // If already granted, disable it
    if (permissions.notifications.status === 'granted') {
      setPermissions(prev => ({ ...prev, notifications: { status: 'default', message: 'Notification permission not set' } }));
      return;
    }
    
    if (permissions.notifications.status === 'denied') {
      alert("Notifications are blocked. To enable:\n\n1. Click the lock icon in your browser's address bar\n2. Set \"Notifications\" to \"Allow\"\n3. Refresh the page");
      return;
    }
    setIsRequestingNotifications(true);
    try {
      const granted = await requestNotificationPermission();
      if (granted) {
        new Notification('Notifications Enabled!', { body: 'You will now receive call notifications', icon: '/favicon.svg' });
      }
      await checkPermissions();
    } catch (error) {
      console.error('Notification request failed:', error);
    } finally {
      setIsRequestingNotifications(false);
    }
  };

  const handleCameraRequest = async () => {
    // If already granted, disable it
    if (permissions.camera.status === 'granted') {
      setPermissions(prev => ({ ...prev, camera: { status: 'prompt', message: 'Camera permission not set' } }));
      return;
    }
    
    if (permissions.camera.status === 'denied') {
      alert("Camera access is blocked. To enable:\n\n1. Click the lock icon in your browser's address bar\n2. Set \"Camera\" to \"Allow\"\n3. Refresh the page");
      return;
    }
    setIsRequestingCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(track => track.stop());
      await checkPermissions();
    } catch (error) {
      console.error('Camera request failed:', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setPermissions(prev => ({ ...prev, camera: { status: 'denied', message: 'Camera access denied by user' } }));
      }
    } finally {
      setIsRequestingCamera(false);
    }
  };

  const handlePushNotificationRequest = async () => {
    // If already subscribed, disable it
    if (permissions.pushNotifications.isSubscribed) {
      setPermissions(prev => ({ ...prev, pushNotifications: { status: 'default', message: 'Push notification permission not set', isSubscribed: false, backendSubscribed: false } }));
      // TODO: Call unsubscribe method when available
      return;
    }
    
    if (permissions.pushNotifications.status === 'denied') {
      alert("Push notifications are blocked. To enable:\n\n1. Click the lock icon in your browser's address bar\n2. Set \"Notifications\" to \"Allow\"\n3. Refresh the page");
      return;
    }
    setIsRequestingPush(true);
    try {
      await pushNotifications.actions.subscribe();
    } catch (error) {
      console.error('Push notification request failed:', error);
    } finally {
      setIsRequestingPush(false);
    }
  };

  const openBrowserSettings = () => {
    alert(
      'To manually enable permissions:\n\n1. Click the lock icon (🔒) or settings icon next to the address bar\n2. Find "Permissions" or "Site Settings"\n3. Enable "Location", "Notifications", and "Camera"\n4. Refresh the page\n\nOn mobile browsers:\n- Look for "Site Settings" in the browser menu\n- Enable location, notification, camera, and push notification permissions'
    );
  };

  return {
    permissions,
    isRequestingLocation,
    isRequestingNotifications,
    isRequestingCamera,
    isRequestingPush,
    iosInstall,
    pushNotifications,
    checkPermissions,
    handleLocationRequest,
    handleNotificationRequest,
    handleCameraRequest,
    handlePushNotificationRequest,
    openBrowserSettings,
  };
};
